import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { createContext } from 'react';

import { getEarnCurrencyOrderApi, getEarnStatisticsApi, getEarnSummaryApi } from '@flipster/apis/v1/earn';
import {
  Apr,
  GetEarnStatisticsResponse,
  GetEarnSummaryResponse,
  PastWeekEarning,
  PendingReduction,
  ProcessingMintRequest,
} from '@flipster/apis/v1/earn/types';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { NonUsdCurrencyList } from '@flipster/utils/constants/currency';
import Num, { BigNumber } from '@flipster/utils/num';

export const EarnCurrenciesContext = createContext<string[]>([]);
export const EarnCurrenciesScope = createScopeFromContext(EarnCurrenciesContext);
export const usdBalanceEmptyThreshold = '0.01';
export const btcBalanceEmptyThreshold = '0.00000001';

export const earnSummaryBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const getEarnSummaryApiAtom = atomWithQuery(() => {
    return {
      queryKey: ['getEarnSummaryApi'],
      queryFn: getEarnSummaryApi,
      select: (res) => res.data,
      refetchOnMount: 'always',
    };
  });

  const earnSummaryAtom = atom((get) => get(getEarnSummaryApiAtom).data);
  const isPendingAtom = atom((get) => get(getEarnSummaryApiAtom).isPending);
  async function refetch() {
    return await store.get(getEarnSummaryApiAtom).refetch();
  }

  return { getEarnSummaryApiAtom, earnSummaryAtom, isPendingAtom, refetch };
});
earnSummaryBunja.debugLabel = 'earnSummaryBunja';

export interface EarnSummaryOfCurrency {
  showAppliedBalanceDetail: boolean;
  balance: BigNumber;
  balanceInUsdt: BigNumber;
  pendingReductions: PendingReduction[];
  aprs: Apr[];
  extraAprs: Apr[];
  baseAprPct: BigNumber;
  maxAprPct: BigNumber;
  appliedAprPct: BigNumber;
  sumOfExtraAprPct: BigNumber;
  hasTradeBonus: boolean;
  isTradeAvailable: boolean;
  isMintAvailable: boolean;
  processingMintRequests: ProcessingMintRequest[];
  isBalanceEmpty: boolean;
  allocations: AprAllocation[];
  noAprAppliedBalance: BigNumber | null;
  maxCap: BigNumber;
}
export function getEarnSummaryOfCurrency(
  currency: string,
  earnSummary?: GetEarnSummaryResponse,
): EarnSummaryOfCurrency {
  const c = earnSummary?.currencies.find((c) => c.currency === currency);
  const showAppliedBalanceDetail = c?.showAppliedBalanceDetail ?? false;
  const balance = Num(c?.balance ?? 0);
  const balanceInUsdt = Num(c?.balanceInUsdt ?? 0);
  const pendingReductions = c?.pendingReductions ?? [];
  const processingMintRequests = c?.processingMintRequests ?? [];
  const aprs = c?.aprs ?? [];
  const extraAprs = aprs.filter((apr) => Num(apr.balanceCap).gt(0) && apr.type !== 'BASE');
  const hasTradeBonus = aprs.some((apr) => apr.type === 'TRADING_BONUS');

  const baseAprPct = Num(aprs.find((apr) => apr.type === 'BASE')?.appliedApr ?? 0).multipliedBy(100);
  const maxCap = Num(aprs.find((apr) => apr.type === 'BASE')?.balanceCap ?? 0);
  const maxAprPct = Num(aprs.reduce((acc, apr) => acc.plus(apr.maxAchievableApr), Num(0))).multipliedBy(100);
  const appliedAprPct = Num(
    aprs.filter((apr) => Num(apr.balanceCap).gt(0)).reduce((acc, apr) => acc.plus(apr.appliedApr), Num(0)),
  ).multipliedBy(100);
  const sumOfExtraAprPct = Num(extraAprs.reduce((acc, apr) => acc.plus(apr.appliedApr), Num(0))).multipliedBy(100);

  // isMintAvailable, isTradeAvailable이 Action buttons 팝업용으로 사용됨
  const isTradeAvailable = c?.isTradeAvailable ?? false;
  const isMintAvailable = c?.isMintAvailable ?? false;

  const isBalanceUnderEmptyThreshold = NonUsdCurrencyList.includes(currency)
    ? balance.lt(btcBalanceEmptyThreshold)
    : balance.lt(usdBalanceEmptyThreshold);
  const isBalanceEmpty = isBalanceUnderEmptyThreshold || aprs.length === 0;

  const allocations = getAprAllocations(aprs);
  const noAprAppliedBalance = getNoAprAppliedBalance(aprs, balance, balanceInUsdt);

  return {
    showAppliedBalanceDetail,
    balance,
    balanceInUsdt,
    pendingReductions,
    aprs,
    extraAprs,
    baseAprPct,
    maxAprPct,
    appliedAprPct,
    sumOfExtraAprPct,
    hasTradeBonus,
    isTradeAvailable,
    isMintAvailable,
    processingMintRequests,
    isBalanceEmpty,
    allocations,
    noAprAppliedBalance,
    maxCap,
  };
}
export interface AprAllocation {
  sumOfAppliedApr: BigNumber;
  aprs: Apr[];
  allocatedBalance: BigNumber;
}
export function getAprAllocations(aprs: Apr[]): AprAllocation[] {
  // 1. appliedBalance 기준 오름차순 정렬
  const sortedAPRs = aprs
    .filter((apr) => Num(apr.appliedBalance).gt(0))
    .sort((a, b) => Num(a.appliedBalance).comparedTo(b.appliedBalance));

  // 2. 세그먼트 계산
  const allocations: AprAllocation[] = [];
  let cumulatedAppliedBalance = Num(0);

  for (let i = 0; i < sortedAPRs.length; i++) {
    const currentAPR = sortedAPRs[i];
    const originalAppliedBalance = Num(currentAPR.appliedBalance);
    // 이미 처리된 잔액보다 작거나 같으면 스킵
    if (originalAppliedBalance.lte(cumulatedAppliedBalance)) {
      continue;
    }
    // 실제 적용 잔액 계산
    const realizedAppliedBalance = originalAppliedBalance.minus(cumulatedAppliedBalance);
    // 누적 APR 계산 (현재 APR + 이후 모든 APR)
    const cumulativeAPR = sortedAPRs.slice(i).reduce((sum, apr) => sum.plus(Num(apr.appliedApr)), Num(0));

    if (cumulativeAPR.gt(0)) {
      allocations.push({
        sumOfAppliedApr: cumulativeAPR,
        aprs: sortedAPRs.slice(i),
        allocatedBalance: realizedAppliedBalance,
      });
    }
    cumulatedAppliedBalance = cumulatedAppliedBalance.plus(realizedAppliedBalance);
  }
  return allocations;
}
function getNoAprAppliedBalance(aprs: Apr[], balance: BigNumber, balanceInUsdt: BigNumber) {
  const noAprAppliedBalanceInUsdt = balanceInUsdt.minus(
    BigNumber.max(...aprs.map((apr) => Num(apr.appliedBalanceInUsdt))),
  );
  if (noAprAppliedBalanceInUsdt.lt(usdBalanceEmptyThreshold)) return null;
  return balance.minus(BigNumber.max(...aprs.map((apr) => Num(apr.appliedBalance))));
}

export const earnStatisticsBunja = bunja(() => {
  const getEarnStatisticsApiAtom = atomWithQuery(() => {
    return {
      queryKey: ['getEarnStatisticsApi'],
      queryFn: getEarnStatisticsApi,
      select: (res) => res.data,
      refetchOnMount: 'always',
    };
  });
  const earnStatisticsAtom = atom((get) => get(getEarnStatisticsApiAtom).data);
  const isPendingAtom = atom((get) => get(getEarnStatisticsApiAtom).isPending);
  return { earnStatisticsAtom, isPendingAtom };
});
earnStatisticsBunja.debugLabel = 'earnStatisticsBunja';

export interface EarnHistoryOfCurrency {
  timestamp: string;
  dailyPayoutAmount: BigNumber;
}
export function getEarnHistoryOfCurrency(
  currency?: string,
  earnStatistics?: GetEarnStatisticsResponse,
): EarnHistoryOfCurrency[] {
  if (!earnStatistics?.currencies) {
    return [];
  }

  const calculateDailyPayoutAmount = (earnings: PastWeekEarning['earnings']): BigNumber => {
    let total = Num(0);
    for (const earning of earnings) {
      total = total.plus(Num(currency ? earning.payoutAmount : earning.payoutAmountInUsdt));
    }
    return total;
  };

  const timestampMap: Record<string, { timestamp: string; dailyPayoutAmount: BigNumber }> = {};

  // 특정 currency가 선택된 경우와 전체 currency 합계인 경우 모두 동일한 로직 사용
  const currenciesToProcess = currency
    ? earnStatistics.currencies.filter((c) => c.currency === currency)
    : earnStatistics.currencies;

  for (const currencyData of currenciesToProcess) {
    for (const history of currencyData.pastWeekEarnings) {
      const timestamp = history.timestamp;

      if (!timestampMap[timestamp]) {
        timestampMap[timestamp] = {
          timestamp,
          dailyPayoutAmount: Num(0),
        };
      }

      // 같은 timestamp에 대해 dailyPayoutAmount 누적
      timestampMap[timestamp].dailyPayoutAmount = timestampMap[timestamp].dailyPayoutAmount.plus(
        calculateDailyPayoutAmount(history.earnings),
      );
    }
  }

  // timestamp 기준으로 정렬하여 EarnHistoryOfCurrency[] 형태로 변환
  return Object.values(timestampMap).sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

export const earnCurrencyOrderBunja = bunja(() => {
  const getEarnCurrencyOrderApiAtom = atomWithQuery(() => {
    return {
      queryKey: ['getEarnCurrencyOrderApi'],
      queryFn: getEarnCurrencyOrderApi,
      select: (res) => res.data,
    };
  });
  const earnCurrencyOrderAtom = atom((get) => get(getEarnCurrencyOrderApiAtom).data);
  const isPendingAtom = atom((get) => get(getEarnCurrencyOrderApiAtom).isPending);
  return { earnCurrencyOrderAtom, isPendingAtom };
});
earnCurrencyOrderBunja.debugLabel = 'earnCurrencyOrderBunja';
